/**
 * Tasks API service
 * Handles communication with the backend for tasks
 */

import { getApiBaseUrl } from '@utils/appBase';
import { devLog } from '@utils/devLog';
import { apiGet, apiPost, apiPut, apiDelete } from './api';

export interface TaskResponse {
	id: number;
	project_id?: number;
	title: string;
	description?: string;
	type: 'task' | 'milestone' | 'phase';
	status: 'not_started' | 'in_progress' | 'completed' | 'blocked' | 'at_risk' | 'delayed';
	start_date?: string;
	end_date?: string;
	due_date?: string;
	duration?: number;
	progress: number;
	dependencies: string[];
	is_critical: boolean;
	plant_id?: string;
	plant_name?: string;
	gardener_ids: number[];
	gardener_names: string[];
	machine_ids: string[];
	machine_names: string[];
	notes?: string;
	created_at: string;
	updated_at: string;
	completed_at?: string;
	project_company_name?: string | null;
	project_company_id?: number | null;
	open_issue_count?: number;
	legal_seasonal_ban_candidate?: boolean;
	legal_seasonal_ban_reason?: string;
}

export interface CreateTaskPayload {
	project_id?: number;
	title: string;
	description?: string;
	type?: 'task' | 'milestone' | 'phase';
	status?: 'not_started' | 'in_progress' | 'completed' | 'blocked' | 'at_risk' | 'delayed';
	start_date?: string;
	end_date?: string;
	due_date?: string;
	duration?: number;
	progress?: number;
	dependencies?: string[];
	is_critical?: boolean;
	plant_id?: string;
	plant_name?: string;
	gardener_ids?: number[];
	machine_ids?: string[];
	machine_names?: string[];
	notes?: string;
	required_skills?: string[];
	required_gardeners?: number;
	legal_seasonal_ban_candidate?: boolean;
	legal_seasonal_ban_reason?: string;
}

export interface UpdateTaskPayload extends Partial<CreateTaskPayload> {}

/**
 * Get all tasks (with optional filters)
 */
export async function getTasks(params?: {
	project_id?: number;
	user_id?: number;
	status?: string;
	requester_user_id?: number;
	company_scope?: 'company' | 'all_companies';
}): Promise<TaskResponse[]> {
	const queryParams = new URLSearchParams();
	if (params?.project_id) queryParams.append('project_id', params.project_id.toString());
	if (params?.user_id) queryParams.append('user_id', params.user_id.toString());
	if (params?.status) queryParams.append('status', params.status);
	if (params?.requester_user_id) queryParams.append('requester_user_id', params.requester_user_id.toString());
	if (params?.company_scope) queryParams.append('company_scope', params.company_scope);
	
	const query = queryParams.toString();
	return apiGet<TaskResponse[]>(`/tasks${query ? '?' + query : ''}`);
}

/**
 * Get tasks for a specific project
 */
export async function getTasksForProject(projectId: number): Promise<TaskResponse[]> {
	return apiGet<TaskResponse[]>(`/tasks/project/${projectId}`);
}

/**
 * Get a specific task by ID
 */
export async function getTaskById(taskId: number): Promise<TaskResponse> {
	return apiGet<TaskResponse>(`/tasks/${taskId}`);
}

/**
 * Create a new task
 */
export async function createTask(payload: CreateTaskPayload): Promise<TaskResponse> {
	return apiPost<TaskResponse>(`/tasks`, payload);
}

/**
 * Update a task
 */
export async function updateTask(taskId: number, payload: UpdateTaskPayload): Promise<TaskResponse> {
	return apiPut<TaskResponse>(`/tasks/${taskId}`, payload);
}

/**
 * Delete a task
 */
export async function deleteTask(taskId: number): Promise<void> {
	return apiDelete<void>(`/tasks/${taskId}`);
}

/**
 * Assign gardener to task
 */
export async function assignGardenerToTask(taskId: number, gardenerUserId: number): Promise<TaskResponse> {
	return apiPost<TaskResponse>(`/tasks/${taskId}/gardeners`, {
		gardener_user_id: gardenerUserId
	});
}

/**
 * Remove gardener from task
 */
export async function removeGardenerFromTask(taskId: number, gardenerUserId: number): Promise<void> {
	return apiDelete<void>(`/tasks/${taskId}/gardeners/${gardenerUserId}`);
}

/**
 * Assign machine to task
 */
export async function assignMachineToTask(taskId: number, machineId: string, machineName?: string): Promise<TaskResponse> {
	return apiPost<TaskResponse>(`/tasks/${taskId}/machines`, {
		machine_id: machineId,
		machine_name: machineName
	});
}

/**
 * Remove machine from task
 */
export async function removeMachineFromTask(taskId: number, machineId: string): Promise<void> {
	return apiDelete<void>(`/tasks/${taskId}/machines/${encodeURIComponent(machineId)}`);
}

/**
 * Task Attachment types
 */
export interface TaskAttachment {
	id: number;
	task_id: number;
	file_name: string;
	file_path: string;
	file_size?: number;
	file_size_formatted?: string;
	mime_type?: string;
	uploaded_by?: number;
	uploaded_by_name?: string;
	description?: string;
	created_at: string;
	updated_at: string;
}

/**
 * Task Issue types
 */
export interface TaskIssue {
	id: number;
	taskId: number;
	projectId?: number | null;
	createdByUserId?: number | null;
	description: string;
	status: 'open' | 'in_review' | 'resolved';
	severity: 'low' | 'medium' | 'high' | 'critical';
	issueType: 'delay' | 'damage' | 'scope_change' | 'cost' | 'other';
	attachmentId?: number | null;
	createdAt: string;
	updatedAt: string;
	createdByName?: string;
	attachmentFileName?: string;
	attachmentFilePath?: string;
	attachmentMimeType?: string;
}

export interface CreateTaskIssuePayload {
	description: string;
	status?: 'open' | 'in_review' | 'resolved';
	severity?: 'low' | 'medium' | 'high' | 'critical';
	issue_type?: 'delay' | 'damage' | 'scope_change' | 'cost' | 'other';
	attachment_id?: number | null;
	created_by_user_id?: number | null;
}

export interface UpdateTaskIssuePayload extends Partial<CreateTaskIssuePayload> {}

/**
 * Get all attachments for a task
 */
export async function getTaskAttachments(taskId: number): Promise<TaskAttachment[]> {
	return apiGet<TaskAttachment[]>(`/tasks/${taskId}/attachments`);
}

/**
 * Upload a file attachment for a task
 */
export async function uploadTaskAttachment(
	taskId: number,
	file: File,
	description?: string,
	uploadedBy?: number
): Promise<TaskAttachment> {
	const API_BASE_URL = getApiBaseUrl();
	
	const formData = new FormData();
	formData.append('file', file);
	if (description) formData.append('description', description);
	if (uploadedBy) formData.append('uploaded_by', uploadedBy.toString());

	const endpoint = `/tasks/${taskId}/attachments`;
	const fullUrl = `${API_BASE_URL}${endpoint}`;
	
	devLog('[API] POST file upload to:', fullUrl);

	const response = await fetch(fullUrl, {
		method: 'POST',
		body: formData,
		credentials: 'include'
		// Don't set Content-Type header - browser will set it with boundary for multipart/form-data
	});

	// Check if response is empty
	const text = await response.text();
	if (!text) {
		throw new Error(`Empty response from server (status: ${response.status})`);
	}

	let data: any;
	try {
		data = JSON.parse(text);
	} catch (e) {
		throw new Error(`Invalid JSON response: ${text.substring(0, 100)}`);
	}

	// Handle both wrapped (ApiResponse) and direct response formats
	if (typeof data === 'object' && data !== null && 'success' in data) {
		const apiResponse = data as { success: boolean; data?: TaskAttachment; error?: string };
		if (!apiResponse.success) {
			throw new Error(apiResponse.error || 'Upload failed');
		}
		if (!response.ok) {
			throw new Error(apiResponse.error || `HTTP error! status: ${response.status}`);
		}
		return apiResponse.data as TaskAttachment;
	}

	// Direct response (for endpoints that don't wrap in ApiResponse)
	if (!response.ok) {
		throw new Error(data.error || `HTTP error! status: ${response.status}`);
	}

	return data as TaskAttachment;
}

/**
 * Check for task conflicts (overlapping tasks)
 */
export interface ConflictCheckPayload {
	project_id: number;
	start_date: string;
	end_date: string;
	exclude_task_id?: number; // For updates, exclude the current task
}

export interface ConflictInfo {
	id: number;
	title: string;
	start_date: string;
	end_date: string;
	status: string;
	gardener_ids: number[];
	gardener_names: string[];
}

export interface ConflictCheckResponse {
	has_conflicts: boolean;
	conflicts: ConflictInfo[];
	conflict_count: number;
}

export async function checkTaskConflicts(payload: ConflictCheckPayload): Promise<ConflictCheckResponse> {
	return apiPost<ConflictCheckResponse>(`/tasks/check-conflicts`, payload);
}

/**
 * Auto-rescheduling types
 */
export interface RescheduleTaskChange {
	task_id: number;
	task_title: string;
	old_start_date: string;
	old_end_date: string;
	new_start_date: string;
	new_end_date: string;
	shift_days: number;
}

export interface CalculateReschedulePayload {
	project_id: number;
	start_date: string;
	end_date: string;
	exclude_task_id?: number;
	reschedule_mode?: 'shift_forward' | 'shift_backward';
}

export interface CalculateRescheduleResponse {
	has_changes: boolean;
	affected_tasks: RescheduleTaskChange[];
	affected_count: number;
	reschedule_mode: string;
}

export interface ApplyReschedulePayload {
	task_changes: RescheduleTaskChange[];
	reason?: string;
}

export interface ApplyRescheduleResponse {
	success: boolean;
	updated_tasks: Array<{
		task_id: number;
		new_start_date: string;
		new_end_date: string;
	}>;
	updated_count: number;
	reason: string;
}

/**
 * Calculate auto-rescheduling proposal
 */
export async function calculateAutoReschedule(payload: CalculateReschedulePayload): Promise<CalculateRescheduleResponse> {
	return apiPost<CalculateRescheduleResponse>(`/tasks/calculate-reschedule`, payload);
}

/**
 * Apply auto-rescheduling to tasks
 */
export async function applyAutoReschedule(payload: ApplyReschedulePayload): Promise<ApplyRescheduleResponse> {
	return apiPost<ApplyRescheduleResponse>(`/tasks/apply-reschedule`, payload);
}

/**
 * Delete a task attachment
 */
export async function deleteTaskAttachment(taskId: number, attachmentId: number): Promise<void> {
	const API_BASE_URL = getApiBaseUrl();
	
	const endpoint = `/tasks/${taskId}/attachments/${attachmentId}`;
	const fullUrl = `${API_BASE_URL}${endpoint}`;
	
	devLog('[API] DELETE', fullUrl);

	const response = await fetch(fullUrl, {
		method: 'DELETE',
		headers: {
			'Content-Type': 'application/json',
		},
		credentials: 'include'
	});

	// Check if response is empty
	const text = await response.text();
	if (!text) {
		if (response.ok) {
			return; // Success with no body
		}
		throw new Error(`Empty response from server (status: ${response.status})`);
	}

	let data: any;
	try {
		data = JSON.parse(text);
	} catch (e) {
		if (response.ok) {
			return; // Success with invalid JSON (probably empty)
		}
		throw new Error(`Invalid JSON response: ${text.substring(0, 100)}`);
	}

	// Handle both wrapped (ApiResponse) and direct response formats
	if (typeof data === 'object' && data !== null && 'success' in data) {
		const apiResponse = data as { success: boolean; error?: string };
		if (!apiResponse.success) {
			throw new Error(apiResponse.error || 'Delete failed');
		}
		if (!response.ok) {
			throw new Error(apiResponse.error || `HTTP error! status: ${response.status}`);
		}
		return;
	}

	// Direct response
	if (!response.ok) {
		throw new Error(data.error || `HTTP error! status: ${response.status}`);
	}

	return;
}

/**
 * List issues for a task
 */
export async function getTaskIssues(taskId: number): Promise<TaskIssue[]> {
	return apiGet<TaskIssue[]>(`/tasks/${taskId}/issues`);
}

/**
 * Create a new issue for a task
 */
export async function createTaskIssue(taskId: number, payload: CreateTaskIssuePayload): Promise<TaskIssue> {
	return apiPost<TaskIssue>(`/tasks/${taskId}/issues`, payload);
}

/**
 * Update an existing task issue
 */
export async function updateTaskIssue(issueId: number, payload: UpdateTaskIssuePayload): Promise<TaskIssue> {
	return apiPut<TaskIssue>(`/task-issues/${issueId}`, payload);
}


